import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format, parse } from "date-fns";

import useFetch from "../useFetch";
import { ToastContext } from "src/context/toastContext";

import { AddCaseProps, ShipmentAttributes } from "src/pages/addCase/types";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";
import { AddCaseResponseProps } from "./types";
import { PracticeDropdownResponse } from "src/components/caseStepTwo/types";



/**
 * @AddNewCase form handling for case data submission
 * @fourth case submit handler
 */
const useCaseSubmitFunc = (practiceDropdownApiRes?: PracticeDropdownResponse) => {
  const navigate = useNavigate()
  const { showToast } = useContext(ToastContext)



  const { loading: addCaseLoading, data: addCaseData, mutate: addNewCase } =
    useFetch<AddCaseResponseProps>(APIEndpoints.ADD_NEW_CASE);

  useEffect(() => {
    if (addCaseData && addCaseData.status === 200) {
      localStorage.removeItem('newCase');
      showToast([`Your request submitted successfully!`], ToastTypes.SUCCESS);
      if (addCaseData.case_ids && addCaseData.case_ids.length > 0)
        navigate(`/cases/${addCaseData.case_ids[0]}/documents/new`);
      else
        navigate('/cases')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCaseData]);

  /** @fourth */
  const caseDataSubmission = (values: AddCaseProps) => {
    const prescription_attributes_attributes = [];
    if (values.prescription_attributes.scheduled_date_of_service) {
      prescription_attributes_attributes.push({
        name: "scheduled_date_of_service",
        value: values.prescription_attributes.scheduled_date_of_service,
      });
    }
    if (values.prescription_attributes.injection_site) {
      prescription_attributes_attributes.push({
        name: "injection_site",
        value: values.prescription_attributes.injection_site,
      });
    }
    const { scheduled_date_of_service, injection_site, diagnosis_code, ...actualPrescription } =
      values.prescription_attributes;

    const required_shipments_attributes: (ShipmentAttributes & { ship_to: string; })[] = [];
    if (values.case_attributes.ship_to) {
      if (values.case_attributes.ship_to === "other" && values.shipments_attributes && values.shipments_attributes.length > 0) {
        required_shipments_attributes.push({
          ...values.shipments_attributes[0],
          ship_to: values.case_attributes.ship_to,
        });
      }

      if (values.case_attributes.ship_to && values.case_attributes.ship_to === "patient") {
        required_shipments_attributes.push({
          ship_to: values.case_attributes.ship_to,
          ship_to_street: values.patient_attributes.address.street,
          ship_to_suite: values.patient_attributes.address.suite || "",
          ship_to_city: values.patient_attributes.address.city,
          ship_to_state: values.patient_attributes.address.state,
          ship_to_zip: values.patient_attributes.address.zip,
        })
      }
  
      if (values.case_attributes.ship_to && values.case_attributes.ship_to === "provider") {
        const practiceData = practiceDropdownApiRes && practiceDropdownApiRes.data && practiceDropdownApiRes.data.length > 0 &&
          practiceDropdownApiRes.data.find((prac) => prac.id === values.doctor_attributes.practice_id)
  
        if (practiceData) {
          required_shipments_attributes.push({
            ship_to: values.case_attributes.ship_to,
            ship_to_street: practiceData.address.street,
            ship_to_suite: practiceData.address.suite || "",
            ship_to_city: practiceData.address.city,
            ship_to_state: practiceData.address.state,
            ship_to_zip: practiceData.address.zip,
          })
        }
      }
    }

    const newValues = {
      ...values,
      drug_name: values.prescription_attributes.drug_name,
      ...(values.procedure_attributes
        ? {
            procedures_attributes: [
              { cpt_code: values.procedure_attributes.cpt_code },
            ],
          }
        : {}),
      prescription_attributes: {
        ...actualPrescription,
        diagnosis_code: values.prescription_attributes.diagnosis_code.join(","),
        written_date: format(parse(values.prescription_attributes.written_date, "MM/dd/yyyy", new Date()), "dd/MM/yyyy"),
        prescription_attributes_attributes,
      },
      shipments_attributes: required_shipments_attributes,
      patient_firstlastname: `${values.patient_attributes.firstname} ${values.patient_attributes.lastname}`,
      patient_fullname: `${values.patient_attributes.firstname} ${values.patient_attributes.middlename} ${values.patient_attributes.lastname}`,
      patient_dob: values.patient_attributes.dob,
      insurances_attributes: values.insurances_attributes?.map(
        (insurance, index) => {
          insurance.insurance_priority = index;
          return insurance;
        }
      ),
      case_attributes_attributes: [
        {
          name: "soc",
          value: values.case_attributes.ship_to,
        },
      ],
      patient_attributes: {
        ...values.patient_attributes,
        ...(values.patient_attributes.patient_email && {
          patient_email: {
            id: values.patient_attributes.patient_email_id,
            email: values.patient_attributes.patient_email,
          },
        }),
      },
    };

    const { case_attributes, procedure_attributes, drug_name, ...actualCaseValues } =
      newValues;

    const allCases: AddCaseProps[] = [];
    values.prescription_attributes.drug_name.forEach((drug) => {
      let qty = values.prescription_attributes.qty;
      if (drug === "DUROLANE") {
        if (values.prescription_attributes.injection_site === "bilateral") qty = 2
        else qty = 1
      }
      else if (drug === "GELSYN") {
        if (values.prescription_attributes.injection_site === "bilateral") qty = 6
        else qty = 3
      }
      allCases.push({
        ...actualCaseValues,
        prescription_attributes: {
          ...actualCaseValues.prescription_attributes,
          drug_name: drug,
          qty,
        }
      } as unknown as AddCaseProps)
    });

    addNewCase({ case: allCases })
  };

  return {
    addCaseLoading,
    caseDataSubmission
  }
}

export default useCaseSubmitFunc
